import { useEffect, useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { useLocation } from 'react-router-dom';
import {
  useShadowRootElements,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { parseTable } from '../utils/parse-table';
import { InteractiveTableView } from './interactive-table-view';
import {
  getRelativeDocsPath,
  matchesAnyPathRestriction,
} from '../utils/path-matching';

const ENABLED_ANNOTATION = 'hm.com/techdocs-interactive-table';
const PATHS_ANNOTATION = 'hm.com/techdocs-interactive-table-paths';
const REQUIRE_MARKER_ANNOTATION =
  'hm.com/techdocs-interactive-table-require-marker';
// Authors opt a single table into table-level precision by wrapping it:
//   <div class="interactive-table" markdown="1"> | ... | </div>
const MARKED_TABLE_SELECTOR = '.interactive-table table';

/**
 * TechDocs `Content` addon: finds plain `<table>`s rendered into the docs
 * shadow root and swaps them out for a React-mounted interactive table
 * (sortable columns, search, per-column filters, pagination, resizable
 * columns - and any links already authored in the source markdown remain
 * clickable). Renders nothing itself - the real output is the DOM mutation.
 *
 * This addon is registered once, globally, on the `techdocs` plugin - it
 * would run on every TechDocs page for every entity in the catalog, not
 * just the one it was built for. To avoid silently changing every team's
 * docs tables, enablement is entirely self-service via annotations on that
 * entity's OWN catalog-info.yaml - not a centrally-maintained list in this
 * repo's app-config.yaml. Any team can turn this on for their own docs by
 * editing their own catalog-info.yaml, with no PR against this repo:
 *
 *   metadata:
 *     annotations:
 *       # Layer 1 (required): turns the addon on for every table on every
 *       # page of this entity.
 *       hm.com/techdocs-interactive-table: 'true'
 *
 *       # Layer 2 (optional): an ALLOWLIST, not an exclude-list - narrows
 *       # down to ONLY the page(s) listed, comma-separated. Omit for every
 *       # page, as in layer 1 alone.
 *       hm.com/techdocs-interactive-table-paths: '/publications,/reference'
 *
 *       # Layer 3 (optional): narrow further to only tables explicitly
 *       # wrapped in the marker convention (see MARKED_TABLE_SELECTOR).
 *       # Omit for every table on eligible page(s).
 *       hm.com/techdocs-interactive-table-require-marker: 'true'
 *
 * For any entity/page/table that doesn't clear all three layers, this is a
 * no-op: no DOM read, no mutation, no rendering.
 *
 * No explicit root.unmount() on component teardown: our own table->div
 * swap fires the shadow root's MutationObserver, which can cause this
 * component to be torn down and remounted by its parent. Since the
 * original <table> is gone by then, an unmount here would permanently
 * empty the container with nothing left to re-render into. The mounted
 * roots are simply discarded along with the shadow root itself when the
 * user navigates away from the page (same approach Backstage's own
 * contrib addons, e.g. LightBox, take).
 */
export const InteractiveTableAddon = () => {
  const { entityRef, entityMetadata } = useTechDocsReaderPage();
  const location = useLocation();
  const tables = useShadowRootElements<HTMLTableElement>(['table']);
  const processedRef = useRef<WeakSet<HTMLTableElement>>(new WeakSet());

  const scoping = useMemo(() => {
    const annotations = entityMetadata.value?.metadata.annotations ?? {};
    const isEnabled = annotations[ENABLED_ANNOTATION] === 'true';

    if (!isEnabled) {
      return { pageEnabled: false, requireMarker: false };
    }

    const pathsAnnotation = annotations[PATHS_ANNOTATION];
    const restrictionsForEntity = pathsAnnotation
      ? pathsAnnotation
          .split(',')
          .map(path => path.trim())
          .filter(Boolean)
      : undefined;
    const relativePath = getRelativeDocsPath(location.pathname, entityRef);
    const pageEnabled =
      !restrictionsForEntity ||
      matchesAnyPathRestriction(relativePath, restrictionsForEntity);

    const requireMarker = annotations[REQUIRE_MARKER_ANNOTATION] === 'true';

    return { pageEnabled, requireMarker };
  }, [entityMetadata.value, entityRef, location.pathname]);

  useEffect(() => {
    if (!scoping.pageEnabled) return;

    tables.forEach(table => {
      if (processedRef.current.has(table)) return;
      processedRef.current.add(table);

      if (scoping.requireMarker && !table.matches(MARKED_TABLE_SELECTOR)) {
        return; // not wrapped in the marker - leave as plain markdown
      }

      const parsed = parseTable(table);

      // Replace with a plain <div>, never a <table>, so this addon's own
      // output can't be picked up again by the `useShadowRootElements`
      // mutation observer and reprocessed.
      const container = document.createElement('div');
      container.dataset.interactiveTable = 'true';
      table.replaceWith(container);

      createRoot(container).render(<InteractiveTableView {...parsed} />);
    });
  }, [tables, scoping]);

  return null;
};
